<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleOpenExport">
          <i class="ri-download-line"></i>
          匯出
        </button>
        <button class="btn btn-primary" @click="handleManualIssue">
                <i class="ri-add-line"></i>
                手動發放
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="toolbar-row">
        <div class="search-box">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="搜尋優惠券代碼、會員名稱、電話、Email、模板名稱..."
            @input="handleSearch"
          />
          <i class="ri-search-line"></i>
        </div>
      </div>
      <div class="toolbar-row">
        <div class="filter-section">
          <label class="filter-label">狀態</label>
          <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
            <option value="">全部狀態</option>
            <option value="unused">未使用</option>
            <option value="transferring">轉送中</option>
            <option value="transferred_out">已轉出</option>
            <option value="used">已使用</option>
            <option value="expired">已過期</option>
            <option value="cancelled">已取消</option>
            <option value="deleted">已刪除</option>
          </select>
        </div>
        <div class="filter-section">
          <label class="filter-label">模板</label>
          <select v-model="filterTemplateId" class="form-control" @change="handleFilterChange">
            <option value="">全部模板</option>
            <option v-for="template in templates" :key="template.id" :value="template.id">
              {{ template.templateName }}
            </option>
          </select>
        </div>
        <div class="filter-section">
          <label class="filter-label">排序欄位</label>
          <select v-model="sortBy" class="form-control" @change="handleFilterChange">
            <option value="issued_at">發放時間</option>
            <option value="used_at">使用時間</option>
            <option value="expired_at">過期時間</option>
            <option value="coupon_code">優惠券代碼</option>
            <option value="status">狀態</option>
            <option value="template_name">模板名稱</option>
            <option value="member_name">會員名稱</option>
          </select>
        </div>
        <div class="filter-section">
          <label class="filter-label">排序方式</label>
          <select v-model="sortOrder" class="form-control" @change="handleFilterChange">
            <option value="DESC">降序</option>
            <option value="ASC">升序</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchIssues">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>優惠券代碼</th>
            <th>模板名稱</th>
            <th>會員資訊</th>
            <th>狀態</th>
            <th>發放時間</th>
            <th>使用時間</th>
            <th>折扣金額</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="issues.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到優惠券發放記錄
            </td>
          </tr>
          <tr v-for="issue in issues" :key="issue.id">
            <td>
              <strong>{{ issue.couponCode }}</strong>
            </td>
            <td>
              <span :class="getCouponTypeBadgeClass(issue.couponType)">
                {{ issue.templateName }}
              </span>
            </td>
            <td>
              <div v-if="issue.memberName" class="member-info">
                <div><strong>{{ issue.memberName }}</strong></div>
                <div class="text-muted">{{ issue.memberPhone || '-' }}</div>
                <div class="text-muted">{{ issue.memberEmail || '-' }}</div>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span :class="getStatusBadgeClass(issue.status)">
                {{ getStatusText(issue.status) }}
              </span>
              <div v-if="issue.cancelReason" class="cancel-reason-text">
                {{ issue.cancelReason }}
              </div>
            </td>
            <td>{{ formatDateTime(issue.issuedAt) }}</td>
            <td>
              <span v-if="issue.usedAt">{{ formatDateTime(issue.usedAt) }}</span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span v-if="issue.discountAmount" class="text-success">
                ${{ formatNumber(issue.discountAmount) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-info"
                  @click="handleView(issue)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>

    <CouponExportModal
      :visible="showExportModal"
      :params="exportParams"
      @close="showExportModal = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError, showWarning } from '@/utils/swal.js';
import CouponExportModal from '@/components/CouponExportModal.vue';

export default {
  name: 'CouponIssues',
  components: { CouponExportModal },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('優惠券發放記錄');
    
    const issues = ref([]);
    const templates = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const filterTemplateId = ref('');
    const sortBy = ref('issued_at');
    const sortOrder = ref('DESC');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);
    const showExportModal = ref(false);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const exportParams = computed(() => {
      const params = { templateId: filterTemplateId.value };
      // 已刪除券不納入匯出（後端亦會排除）
      if (filterStatus.value && filterStatus.value !== 'deleted') {
        params.status = filterStatus.value;
      }
      return params;
    });

    const handleOpenExport = () => {
      if (!filterTemplateId.value) {
        showWarning('請先選擇模板', '匯出用券名單前，請先在「模板」篩選中選擇一個券模板');
        return;
      }
      showExportModal.value = true;
    };

    const fetchTemplates = async () => {
      try {
        const response = await couponService.getTemplates({ pageSize: 100 });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取模板列表失敗:', err);
      }
    };

    const fetchIssues = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        if (filterTemplateId.value) {
          params.templateId = filterTemplateId.value;
        }

        if (sortBy.value) {
          params.sortBy = sortBy.value;
        }

        if (sortOrder.value) {
          params.order = sortOrder.value;
        }

        const response = await couponService.getIssues(params);

        if (response && response.success && response.data) {
          issues.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;
        } else {
          error.value = response?.message || '獲取優惠券發放記錄失敗';
        }
      } catch (err) {
        console.error('獲取優惠券發放記錄失敗:', err);
        error.value = err.message || '獲取優惠券發放記錄失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchIssues();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchIssues();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchIssues();
      }
    };

    const handleManualIssue = () => {
      router.push('/client/coupons/issues/manual');
    };

    const handleView = (issue) => {
      router.push(`/client/coupons/issues/${issue.id}`);
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return Number(num).toLocaleString('zh-TW');
    };

    const getStatusText = (status) => {
      const statusMap = {
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消',
        deleted: '已刪除'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        unused: 'badge badge-success',
        transferring: 'badge badge-warning',
        transferred_out: 'badge badge-info',
        used: 'badge badge-primary',
        expired: 'badge badge-warning',
        cancelled: 'badge badge-secondary',
        deleted: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    const getCouponTypeBadgeClass = (type) => {
      const classMap = {
        discount: 'badge badge-primary',
        fixed_amount: 'badge badge-success',
        free_shipping: 'badge badge-info',
        gift: 'badge badge-warning'
      };
      return classMap[type] || 'badge badge-secondary';
    };

    onMounted(() => {
      if (route.query.status) {
        filterStatus.value = route.query.status;
      }
      fetchTemplates();
      fetchIssues();
    });

    return {
      issues,
      templates,
      loading,
      error,
      searchQuery,
      filterStatus,
      filterTemplateId,
      sortBy,
      sortOrder,
      currentPage,
      totalPages,
      total,
      pageTitle,
      showExportModal,
      exportParams,
      handleOpenExport,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleManualIssue,
      handleView,
      formatDateTime,
      formatNumber,
      getStatusText,
      getStatusBadgeClass,
      getCouponTypeText,
      getCouponTypeBadgeClass,
      fetchIssues
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

/* 此頁面有特殊的工具欄樣式（垂直排列、背景色） */
.page-toolbar {
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.toolbar-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 300px;
  max-width: 600px;
  position: relative;
}

.search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
}

.filter-label {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin: 0;
}

.filter-section .form-control {
  width: 100%;
}

.cancel-reason-text {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
  max-width: 160px;
  white-space: normal;
  word-break: break-all;
}

.member-info {
  font-size: 14px;
}

.member-info div {
  margin-bottom: 2px;
}

.text-muted {
  color: #999;
  font-size: 14px;
}

.text-success {
  color: #28a745;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}
</style>
